import { useState, useCallback } from 'react';

/**
 * A client idempotency key for one logical event-write intent.
 *
 * The PWA's background-sync queue replays a failed POST when
 * connectivity returns, and the operator has usually retried by hand
 * in the meantime, so without a stable key an additive event
 * (PersonArrived, FlagRaised, NoteAdded, DocumentAttached, …) lands
 * twice. The server dedupes on (person_id, client_event_id), so the
 * key must be the SAME across the original request, its queued replay,
 * and a manual retry of that same action — but DIFFERENT for the next
 * distinct action.
 *
 * Usage: pass `key` as `client_event_id`, then call `renew()` only
 * after a successful submit. Retaining the key across failures lets a
 * manual retry dedupe the queued original; renewing after success
 * gives the next action a fresh key. The key is per (person,
 * client_event_id), so reusing one across different people is
 * harmless — they dedupe in separate scopes.
 *
 * Undo/clear events (ArrivalUndone, NoteRemoved, FlagCleared,
 * LeftEarlyUndone) don't need a key: they reference a target event id,
 * so a replay is a no-op the projector ignores.
 */
export function useIntentKey(): { key: string; renew: () => void } {
  const [key, setKey] = useState(() => crypto.randomUUID());
  const renew = useCallback(() => setKey(crypto.randomUUID()), []);
  return { key, renew };
}
